import { memo, MouseEvent, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback } from "@libs/zapi-ui/ds/components/avatar";
import { formatRelativeTime } from "@/zapi-frontend/utils/dates";
import { TComment } from "../model/types/types";
import { getUserFullName, getUserInitials } from "../lib/lib";
import { ClickAwayListener } from "@libs/zapi-ui/custom";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { CommentActions } from "./_CommentActions";
import { ReplyEditInputField } from "./_ReplyEditInputField";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { CommentReplies } from "./_CommentReplies";
import { useScopedTimeout } from "@/zapi-frontend/shared/hooks/useScopedTimeout";
import { scrollIntoView } from "@/zapi-frontend/shared/lib/scrollIntoView";
import { findCommentAnchor } from "../model/store/commentAnchorsStore";
import { flashCommentHighlight, scrollRangeIntoView } from "../lib/commentHighlights";

type TCommentProps = { comment: TComment; onDelete: (id: string) => void; markedText: string };

export const Comment = memo((props: TCommentProps) => {
  const { comment, onDelete, markedText } = props;
  const { t } = useTranslation("projectHome");

  const { firstName, lastName, id: userId } = useMeStore();
  const { runTimeout } = useScopedTimeout();

  const replyFieldRef = useRef<HTMLTextAreaElement>(null);
  const editFieldRef = useRef<HTMLTextAreaElement>(null);

  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [isShake, setIsShake] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const isCanEdit = comment.author.id === userId;

  const handleReplyToComment = useCallback(() => {
    setIsShake(false);
    setIsReplyOpen(true);

    if (replyFieldRef.current) replyFieldRef.current.focus();
  }, []);

  const handleClickAway = useCallback(() => {
    if (!isReplyOpen && !isEditMode) return;
    const currentReplyValue = replyFieldRef.current?.value?.trim() ?? "";

    if (currentReplyValue.length === 0 && !isEditMode) {
      setIsReplyOpen(false);
      return;
    }

    if (isShake) return;
    setIsShake(true);
    runTimeout(() => setIsShake(false), 5000);
  }, [isReplyOpen, isShake, isEditMode, runTimeout]);

  function handleMarkedTextClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    const el = document.querySelector(`[data-persistent-comment-id="${comment.id}"]`) as HTMLSpanElement | null;
    if (!el) {
      const anchor = findCommentAnchor(comment.id);
      if (!anchor) return;
      scrollRangeIntoView(anchor.range);
      runTimeout(() => flashCommentHighlight(anchor.range), 300);
      return;
    }
    scrollIntoView(el, () => {
      el.style.backgroundColor = "var(--innovera-yellow300)";
      void el.offsetWidth;

      runTimeout(() => {
        el.style.backgroundColor = "var(--innovera-yellow100)";
      }, 1000);
    });
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway} triggerOn="click" insideSelectors={["[data-base-ui-portal]"]}>
      <article
        className={cn(
          "group/comment rounded-xl p-3 transition-colors",
          isReplyOpen ? "bg-muted/50 ring-1 ring-foreground/5" : "cursor-pointer hover:bg-muted/50",
          isShake && "shake-animation"
        )}
        onClick={handleReplyToComment}
        data-testid={`comment-${comment.id}`}
        id={comment.id}
      >
        <header className="flex items-center gap-2.5">
          <Avatar className="size-7">
            <AvatarFallback className="typo-caption">{getUserInitials(comment.author)}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-1 items-baseline gap-1.5">
            <span className="typo-label-md truncate text-foreground">{getUserFullName(comment.author)}</span>
            <span className="typo-caption shrink-0 text-muted-foreground">{formatRelativeTime(comment.createdAt)}</span>
          </div>
          <CommentActions
            commentId={comment.id}
            onDelete={onDelete}
            isCanEdit={isCanEdit}
            withResolveButton
            isVisible={isReplyOpen}
            onEdit={() => setIsEditMode(true)}
          />
        </header>
        <div className="ms-[2.375rem] mt-1 flex flex-col gap-2.5">
          {markedText && (
            <button
              type="button"
              data-testid={`comment-${comment.id}-marked-text-button`}
              onClick={handleMarkedTextClick}
              title={t("comments.showInChapter")}
              className="typo-body-sm rounded-e-md border-s-2 border-primary bg-primary-50 px-2.5 py-1 text-start text-muted-foreground transition-colors hover:bg-primary-100 dark:bg-primary-950/40"
            >
              {markedText}
            </button>
          )}
          {isEditMode && userId ? (
            <ReplyEditInputField
              ref={editFieldRef}
              userId={userId}
              commentId={comment.id}
              commentContent={comment.content}
              isEditField
              onSubmit={() => setIsEditMode(false)}
              onCancel={() => setIsEditMode(false)}
            />
          ) : (
            <p className="typo-body-sm whitespace-pre-wrap text-foreground">{comment.content}</p>
          )}
          {comment.replies.length > 0 && userId && (
            <CommentReplies
              commentReplies={comment.replies}
              parentId={comment.id}
              isReplyOpen={isReplyOpen}
              userId={userId}
              onDelete={onDelete}
            />
          )}
          {isReplyOpen && userId && (
            <ReplyEditInputField
              ref={replyFieldRef}
              userId={userId}
              firstName={firstName}
              lastName={lastName}
              commentId={comment.id}
              onCancel={() => setIsReplyOpen(false)}
            />
          )}
        </div>
      </article>
    </ClickAwayListener>
  );
});
